import React, {useState} from "react"
import {isNull} from "lodash"

import {H2, H4, H5} from "@irati/ui/src/Typography/Heading"
import {FormattedCurrency, FormattedMessage, injectIntl} from "@irati/utils/lang"
import {Body, Label} from "@irati/ui/src/Typography"
import mfaService from "@irati/auth/services/mfa"
import {Checkbox, Input, ValidationButton} from "@irati/ui/src/FormItem"
import Button from "@irati/ui/src/Button"

import {InjectIntlProps} from "@irati/utils/lang/injectIntl"
import Flex from "@irati/ui/src/Flex"
import {noDecimal} from "@irati/utils/validation/regex/number"
import Validation from "@irati/utils/validation"
import WalletBallance from "@irati/wallet/apollo/query/Wallet"
import authentication from "@irati/auth"
import {PaymentCurrencyType, Recognition, User, WalletEntityType} from "@irati/types"
import {Caption} from "@irati/ui/src/Labels"
import {Card, Col} from "@irati/ui/src/Layout"
import Form from "@irati/ui/src/Form"
import Loading from "@irati/ui/src/Loading"
import YouNeedToMfa from "@irati-web/mfa/components/Error/YouNeedToMfa"
import {useTheme} from "@irati/ui/src/global/useTheme"
import walletService from "@irati/wallet/graph/wallet"
import RecipientsList from "../../RecipientsList"
import {StyledFlex, Separator} from "./styled"
import RecognitionDetails from "./RecognitionDetails"
import NewRecognitionDetails from "./NewRecognitionDetails"
import attachRewardToRecognition from "@irati-web/recognition/src/utils/recognition/attachReward"

interface Props extends InjectIntlProps {
  onClickButton: () => void
  onRewardCreated: () => void
  onErrorReward: () => void
  recognition: Recognition
  newRecognition?: boolean
}
const MIN_AMOUNT = 1
const distributeRewardPointsSchema = (pointsInWallet: number, numberOfUsers: number) => {
  return Validation.string("platform.validation.number.nonZeroValue")
    .matches(noDecimal, "platform.validation.integersOnly")
    .test("Positive, value above 1 and no decimal", "platform.validation.number.minOne", (val) => val >= MIN_AMOUNT || isNull(val))
    .test(
      "Value cannot exceed wallet total",
      "recognition.reward.admin.distribute.validation.pointsPerUser.max",
      (val) => val * numberOfUsers <= pointsInWallet
    )
}

const RewardRecognition: React.FC<Props> = (props) => {
  const {formatMessage, onClickButton, recognition, onRewardCreated, onErrorReward, newRecognition} = props
  const theme = useTheme()
  const [pointsPerUser, setPointsPerUser] = useState<number>()
  const [submit, setSubmit] = useState<Promise<any>>()
  const [recipientsList, setRecipientsList] = useState<User[]>(recognition.receivers?.results!)
  const [isChecked, setIsChecked] = useState(false)
  const [mfaLoad, setMfaLoad] = useState<Promise<any>>()

  const handlePointsPerUser = (newPointsPerUser: string) => {
    setPointsPerUser(parseInt(newPointsPerUser, 10) || 0)
  }
  const executeReward = async () => {
    const destinationUserIds = recipientsList.map(({uuid}) => uuid)
    if (!pointsPerUser || !isChecked || !destinationUserIds.length) {
      return
    }
    const mfaHasFlag = await mfaService.hasFlag()
    if (!mfaHasFlag) {
      handleMFARetry()
      return
    }
    const rewardPromise = attachRewardToRecognition(destinationUserIds!, pointsPerUser, recognition)
      .then(() => {
        onRewardCreated()
        setTimeout(() => {
          // This call is gonna update the cached points for Reward Wallet. We put a timeout because sometimes the wallet is not coming back as updated.
          walletService.get(authentication.user().uuid!, PaymentCurrencyType.Rp, WalletEntityType.Reward, {fetchPolicy: "network-only"})
        }, 2000)
      })
      .catch(onErrorReward)
    setSubmit(rewardPromise)
  }
  const handleMFARetry = () => setMfaLoad(mfaService.hasFlag())
  const onDeleteRecipient = (uuidToRemove: string) => {
    const newRecipientsList = recipientsList.filter(({uuid}) => uuid !== uuidToRemove)
    setRecipientsList(newRecipientsList)
  }
  if (!recognition?.uuid) {
    return null
  }

  const totalToPay = pointsPerUser ? pointsPerUser * 100 * recipientsList.length : 0
  const headerTranslation = newRecognition
    ? "recognition.popup.createRecognitionManager.success.headerReward"
    : "recognition.popup.attachRewardRecognition.headerReward"
  const secondCTAText = newRecognition ? "recognition.popup.createRecognition.success.rewardAgainButton" : "platform.button.skip"

  return (
    <Loading load={mfaLoad} Error={() => <YouNeedToMfa onRetry={handleMFARetry} hasShadow={false} />}>
      <StyledFlex>
        <Col xs={12} sm={4} padding="0">
          {newRecognition ? <NewRecognitionDetails /> : <RecognitionDetails recognition={recognition} />}
        </Col>
        <Col xs={12} sm={1} padding="0">
          <Flex justifyContent="center" height="100%">
            <Separator />
          </Flex>
        </Col>
        <Col xs={12} sm={7} padding="0">
          <H2 textAlign="center" marginBottom={4}>
            <FormattedMessage id={headerTranslation} />
          </H2>
          <Flex direction="column" gap={theme.spacing.three}>
            <Flex direction="column">
              <H4 marginBottom={2}>
                <FormattedMessage id="recognition.recipients.label" />
              </H4>
              <RecipientsList recipients={recipientsList} />
            </Flex>
            <Card tiny>
              <Flex justifyContent="space-between" alignItems="center">
                <Flex direction="column">
                  <H5>
                    <FormattedMessage id="recognition.reward.intro.available" />
                  </H5>
                  <Caption flush>
                    <FormattedMessage id="platform.reward.managerWidget.balanceDescription" />
                  </Caption>
                </Flex>
                <WalletBallance
                  variables={{userId: authentication.user().uuid, currency: PaymentCurrencyType.Rp, entityType: WalletEntityType.Reward}}>
                  {(apollo) => (
                    <Body weight="bold">
                      <FormattedCurrency value={apollo.data.wallet.amount} currency={PaymentCurrencyType.Rp} />
                    </Body>
                  )}
                </WalletBallance>
              </Flex>
            </Card>
            <Flex gap={theme.spacing.two}>
              <Flex direction="column" flex="1">
                <Label large>
                  <FormattedMessage id="recognition.reward.admin.distribute.enterPoints.title" />
                </Label>
                <WalletBallance
                  variables={{userId: authentication.user().uuid, currency: PaymentCurrencyType.Rp, entityType: WalletEntityType.Reward}}>
                  {(apollo) => {
                    return (
                      <Form>
                        <Input
                          name="pointsPerUser"
                          type="number"
                          min="1"
                          flush
                          value={String(pointsPerUser)}
                          placeholder="recognition.reward.admin.distribute.enterPoints.placeholder"
                          validation={distributeRewardPointsSchema(Math.round(apollo.data.wallet.availableBalance), recipientsList.length)}
                          onChange={handlePointsPerUser}
                        />
                      </Form>
                    )
                  }}
                </WalletBallance>
              </Flex>
              <Flex direction="column" marginTop={3}>
                <Label large>
                  <FormattedMessage id="recognition.reward.admin.card.topup.totalToPay" />
                </Label>
                <Flex alignItems="center">
                  <WalletBallance
                    variables={{
                      userId: authentication.user().uuid,
                      currency: PaymentCurrencyType.Rp,
                      entityType: WalletEntityType.Reward
                    }}>
                    {(apollo) => (
                      <Body weight="bold" color={totalToPay > apollo.data.wallet.amount ? theme.colors.tertiary.r_400 : ""}>
                        <FormattedCurrency value={totalToPay} currency={PaymentCurrencyType.Rp} />
                      </Body>
                    )}
                  </WalletBallance>
                </Flex>
              </Flex>
            </Flex>
            <Flex>
              <Form>
                <Checkbox
                  name="rewardAccord"
                  checked={isChecked}
                  validation={Validation.bool().oneOf([true], formatMessage({id: "platform.defaultValidation.required"}))}
                  onChange={() => setIsChecked(!isChecked)}>
                  <Body small display="inline">
                    <FormattedMessage id="recognition.reward.admin.distribute.checkbox.title" />
                  </Body>
                </Checkbox>
              </Form>
            </Flex>
          </Flex>
          <Flex gap={theme.spacing.two} direction="column">
            <ValidationButton primary small full onClick={executeReward} load={submit}>
              <FormattedMessage id="recognition.popup.createRecognition.success.sendRewardButton" />
            </ValidationButton>
            <Button small full onClick={onClickButton}>
              <FormattedMessage id={secondCTAText} />
            </Button>
          </Flex>
        </Col>
      </StyledFlex>
    </Loading>
  )
}

export default injectIntl(RewardRecognition)
